import axios from 'axios';
import SecureLS from 'secure-ls';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : 'http://localhost:8000/api';
const ls = new SecureLS({ encodingType: 'aes' });

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

axiosInstance.interceptors.request.use((config) => {
  const token = ls.get('authToken');
  
  // Skip adding Authorization header for authentication and registration endpoints
  const authEndpoints = [
    '/account/auth/login/',
    '/account/create/',
    '/account/register/',
    '/account/auth/forgot-password/',
    '/account/auth/reset-password/'
  ];
  
  const urlPath = config.url?.split('?')[0];
  if (authEndpoints.includes(urlPath)) {
    return config;
  }
  
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  } else {
  }

  // Add X-Family-Id header for multi-family context
  // Skip if caller already set the header explicitly (e.g. cross-family fetches)
  if (!config.headers['X-Family-Id']) {
    let familyId = localStorage.getItem('familyId');
    if (!familyId) {
      // Fallback: try to get from families stored in SecureLS
      try {
        const families = ls.get('families');
        if (families && families.length > 0) {
          familyId = families[0].id;
          localStorage.setItem('familyId', familyId);
        }
      } catch { /* ignore */ }
    }
    if (familyId) {
      config.headers['X-Family-Id'] = familyId;
    }
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

export const createFamilyMember = async (familyMemberData) => {
  try {
    const response = await axiosInstance.post('/genealogy/people/create_with_relationship/', familyMemberData);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.error || error.response.data.detail || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

export const saveLetter = async (letterData) => {
  try {
    const response = await axiosInstance.post('/letters/create/', {
      recipient_id: letterData.recipientId,
      content: letterData.content,
      delivery_event: letterData.deliveryEvent,
      delivery_date: letterData.deliveryDate,
      status: letterData.status || 'draft'
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.error || error.response.data.detail || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

export const getMyRelationships = async () => {
  try {
    // Debug: Check if auth token exists
    const token = ls.get('authToken');
    
    const response = await axiosInstance.get('/genealogy/people/my_relationships/');
    
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      console.error("Error status:", error.response.status);
      console.error("Error headers:", error.response.headers);
      throw new Error(error.response.data.message || error.response.data.error || error.response.data.detail || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

export const register = async (userData) => {
  try {
    const response = await axiosInstance.post('/account/create/', userData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'An unexpected error occurred');
  }
};

export const registerWithFamily = async (familyData) => {
  try {
    const response = await axiosInstance.post('/account/register/', familyData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'An unexpected error occurred');
  }
};

export const sendInvitation = async (invitationData) => {
  try {
    const response = await axiosInstance.post('/account/invitations/send/', invitationData);
    return response.data;
  } catch (error) {
    console.error('Invitation API error:', error);
    throw new Error(error.response?.data?.message || 'An unexpected error occurred');
  }
};

export const createInvitation = async (invitationData) => {
  try {
    // Check if auth token exists
    const token = ls.get('authToken');
    
    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }

    const response = await axiosInstance.post('/genealogy/people/create_invitation/', invitationData);
    return response.data;
  } catch (error) {
    console.error('Create invitation API error:', error);
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    throw new Error(error.response?.data?.message || error.message || 'An unexpected error occurred');
  }
};

export const getRelationshipTypes = async () => {
  try {
    
    const response = await axiosInstance.get('/genealogy/relationship-types/');
    
    return response.data;
  } catch (error) {
    console.error('Error fetching relationship types:', error);
    console.error('Error response:', error.response);
    console.error('Error status:', error.response?.status);
    throw new Error(error.response?.data?.message || 'An unexpected error occurred');
  }
};

export const getFamilyRelationshipTypes = async () => {
  try {
    
    const response = await axiosInstance.get('/genealogy/relationship-types/family_types/');
    
    return response.data;
  } catch (error) {
    console.error('Error fetching family relationship types:', error);
    console.error('Error response:', error.response);
    console.error('Error status:', error.response?.status);
    throw new Error(error.response?.data?.message || 'An unexpected error occurred');
  }
};

export const login = async (credentials) => {
  try {
    const response = await axiosInstance.post('/account/auth/login/', credentials);
    if (response.data && response.data.token) {
      ls.set('authToken', response.data.token);
    }
    return response.data;
  } catch (error) {
    // Preserve structured error data for the login page
    const err = new Error(error.response?.data?.error || 'Invalid email or password');
    err.response = error.response;
    throw err;
  }
};

export async function logout() {
  try {
    const token = ls.get('authToken');
    if (!token) {
      throw new Error('User is not authenticated');
    }
    
    const response = await axiosInstance.post('/account/auth/logout/');
    ls.remove('authToken');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'An unexpected error occurred');
  }
}

export const createTimeCapsule = async (capsuleData) => {
  try {
    // Map delivery event to delivery type
    // deliver_on expects: DATE, EVENT, MILESTONE, or INSTANT
    let deliverOn = 'INSTANT';
    if (capsuleData.deliveryEvent) {
      if (capsuleData.deliveryEvent === 'custom') {
        deliverOn = 'DATE';
      } else if (capsuleData.deliveryEvent === 'milestone') {
        deliverOn = 'MILESTONE';
      } else {
        deliverOn = 'EVENT';
      }
    }

    const response = await axiosInstance.post('/timecapsules/', {
      receiver: capsuleData.recipientId || null,
      message: capsuleData.content,
      deliver_on: deliverOn,
      delivery_event: capsuleData.deliveryEvent || null,
      delivery_datetime: capsuleData.deliveryDate || null,
      milestone: capsuleData.milestoneId || null,
      title: capsuleData.title || null,
      status: capsuleData.status || 'DRAFT',
      media_files: capsuleData.mediaFileIds || [],
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      const errorMessage = error.response.data.status?.[0] || 
                          error.response.data.message || 
                          JSON.stringify(error.response.data);
      throw new Error(errorMessage);
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

export const getMyTimeCapsules = async () => {
  try {
    // Use /sent/ endpoint to get only capsules where user is the sender
    const response = await axiosInstance.get('/timecapsules/sent/');
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.error || error.response.data.detail || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Get time capsules where the current user is the recipient ("For You" letters)
export const getReceivedTimeCapsules = async () => {
  try {
    const response = await axiosInstance.get('/timecapsules/received/');
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.error || error.response.data.detail || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Get count of pending time capsules for the recipient (mystery vault)
export const getPendingCapsuleCount = async () => {
  try {
    const response = await axiosInstance.get('/timecapsules/pending_count/');
    return response.data.pending_count || 0;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.error || error.response.data.detail || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

// Get family time capsule activity (per-member capsule counts)
export const getFamilyCapsuleActivity = async () => {
  const response = await axiosInstance.get('/timecapsules/family_activity/');
  return response.data;
};

// Get a single time capsule by ID
export const getTimeCapsule = async (capsuleId) => {
  try {
    const response = await axiosInstance.get(`/timecapsules/${capsuleId}/`);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.error || error.response.data.detail || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

// Update a time capsule
export const updateTimeCapsule = async (capsuleId, capsuleData) => {
  try {
    let deliverOn = 'INSTANT';
    if (capsuleData.deliveryEvent) {
      if (capsuleData.deliveryEvent === 'custom') {
        deliverOn = 'DATE';
      } else if (capsuleData.deliveryEvent === 'milestone') {
        deliverOn = 'MILESTONE';
      } else {
        deliverOn = 'EVENT';
      }
    }

    const response = await axiosInstance.patch(`/timecapsules/${capsuleId}/`, {
      receiver: capsuleData.recipientId || null,
      message: capsuleData.content,
      deliver_on: deliverOn,
      delivery_event: capsuleData.deliveryEvent || null,
      delivery_datetime: capsuleData.deliveryDate || null,
      milestone: capsuleData.milestoneId || null,
      title: capsuleData.title || null,
      status: capsuleData.status || 'DRAFT',
      media_files: capsuleData.mediaFileIds || [],
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.error || error.response.data.detail || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

// Add media to a time capsule
export const addCapsuleMedia = async (capsuleId, mediaFileId) => {
  try {
    const response = await axiosInstance.post(`/timecapsules/${capsuleId}/add_media/`, {
      media_file_id: mediaFileId
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.error || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

// Remove media from a time capsule
export const removeCapsuleMedia = async (capsuleId, mediaFileId) => {
  try {
    const response = await axiosInstance.post(`/timecapsules/${capsuleId}/remove_media/`, {
      media_file_id: mediaFileId
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.error || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

// =====================================================
// Stewardship API Functions
// =====================================================

/**
 * Get capsules where current user is steward and sender is unavailable
 * @returns {Object} { capsules: [], summary: {} }
 */
export const getStewardedCapsules = async () => {
  try {
    const response = await axiosInstance.get('/timecapsules/stewarded/');
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.error || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

const stewardshipError = (error) => {
  if (error.response) {
    const d = error.response.data;
    if (d && d.error) return new Error(d.error);
    if (d && typeof d === 'object') {
      const first = Object.values(d).flat()[0];
      if (typeof first === 'string') return new Error(first);
    }
    return new Error(JSON.stringify(d));
  }
  return new Error('An unexpected error occurred');
};

/**
 * Sender-side stewardship settings: toggle, designations, other stewards on my letters (counts only), my steward summary
 * @returns {Object} { enabled, designations: [{id, role, person_id, first_name, last_name, letter_count}],
 *                     other_stewards: [{person_id, first_name, last_name, letter_count, backup_letter_count}],
 *                     total_letters, letters_without_steward, letters_without_backup, summary }
 */
export const getStewardship = async () => {
  try {
    const response = await axiosInstance.get('/stewardship/');
    return response.data;
  } catch (error) {
    throw stewardshipError(error);
  }
};

/**
 * Turn default stewardship for new letters on or off (existing letters are not changed)
 * @param {boolean} enabled
 */
export const setStewardshipEnabled = async (enabled) => {
  try {
    const response = await axiosInstance.patch('/stewardship/', { enabled });
    return response.data;
  } catch (error) {
    throw stewardshipError(error);
  }
};

/** Family members who can be named steward: [{person_id, first_name, last_name}] */
export const getEligibleStewards = async () => {
  try {
    const response = await axiosInstance.get('/stewardship/eligible/');
    return response.data.eligible;
  } catch (error) {
    throw stewardshipError(error);
  }
};

/** Name a Primary or Backup steward. Returns the stewardship payload plus letters_updated. */
export const addSteward = async (personId, role, applyToUnassigned) => {
  try {
    const response = await axiosInstance.post('/stewardship/designations/', {
      person_id: personId,
      role,
      apply_to_unassigned: applyToUnassigned,
    });
    return response.data;
  } catch (error) {
    throw stewardshipError(error);
  }
};

/** Replace the steward on a designation. */
export const changeSteward = async (designationId, personId, applyToUnassigned, moveExisting) => {
  try {
    const response = await axiosInstance.patch(`/stewardship/designations/${designationId}/`, {
      person_id: personId,
      apply_to_unassigned: applyToUnassigned,
      move_existing: moveExisting,
    });
    return response.data;
  } catch (error) {
    throw stewardshipError(error);
  }
};

/** Remove a designation. reassignTo: a person id to move its letters to, or 'none' to clear them. */
export const removeSteward = async (designationId, reassignTo) => {
  try {
    const response = await axiosInstance.delete(`/stewardship/designations/${designationId}/`, {
      params: reassignTo ? { reassign_to: reassignTo } : {},
    });
    return response.data;
  } catch (error) {
    throw stewardshipError(error);
  }
};

/**
 * Manually deliver a time capsule (for stewards when milestone already passed)
 * @param {string} capsuleId - The capsule ID
 * @returns {Object} { status: 'delivered', capsule: {}, delivered_by: 'steward'|'sender' }
 */
export const deliverCapsule = async (capsuleId) => {
  try {
    const response = await axiosInstance.post(`/timecapsules/${capsuleId}/deliver/`);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.error || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Complete/confirm a milestone (triggers delivery of waiting capsules)
 * @param {string} milestoneId - The milestone ID
 * @param {string} completedDate - Optional completion date (ISO format)
 * @returns {Object} { status: 'completed', milestone: {}, capsules_delivered: number }
 */
export const completeMilestone = async (milestoneId, completedDate = null) => {
  try {
    const data = completedDate ? { completed_date: completedDate } : {};
    const response = await axiosInstance.post(`/milestones/${milestoneId}/complete/`, data);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.error || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Update a family member's availability status (e.g., mark as deceased)
 * @param {string} familyId - The family ID
 * @param {string} personId - The person ID
 * @param {Object} data - { new_status: 'DECEASED'|'UNABLE'|'ACTIVE', reason?: string, effective_date?: string }
 * @returns {Object} { status: 'success', person: {}, affected_capsules: number }
 */
export const updateMemberStatus = async (familyId, personId, data) => {
  try {
    const response = await axiosInstance.post(`/families/${familyId}/members/${personId}/status/`, data);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.error || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Contest a status change (e.g., "I'm not actually deceased!")
 * @param {string} familyId - The family ID
 * @param {string} personId - The person ID
 * @returns {Object} { status: 'success', person: {}, message: string }
 */
export const contestMemberStatus = async (familyId, personId) => {
  try {
    const response = await axiosInstance.post(`/families/${familyId}/members/${personId}/contest/`);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.error || JSON.stringify(error.response.data));
    }
    throw new Error('An unexpected error occurred');
  }
};

// =====================================================
// Family Members API
// =====================================================

export const getFamilyMembers = async (familyId) => {
  const response = await axiosInstance.get(`/families/${familyId}/members/`);
  return response.data;
};

// Family-admin member editing + profile change history
export const getMemberForEdit = async (familyId, personId) => {
  const response = await axiosInstance.get(`/families/${familyId}/members/${personId}/edit/`);
  return response.data;
};

export const updateMemberProfile = async (familyId, personId, changes) => {
  const response = await axiosInstance.patch(`/families/${familyId}/members/${personId}/edit/`, changes);
  return response.data;
};

export const removeMemberFromFamily = async (familyId, personId) => {
  const response = await axiosInstance.delete(`/families/${familyId}/members/${personId}/edit/`);
  return response.data;
};

export const getMemberHistory = async (familyId, personId, cursor) => {
  const response = await axiosInstance.get(`/families/${familyId}/members/${personId}/history/`, {
    params: cursor ? { cursor } : {},
  });
  return response.data;
};

// =====================================================
// Profile & Family Settings API Functions
// =====================================================

export const getUserProfile = async () => {
  const response = await axiosInstance.get('/account/api/accounts/profile/');
  return response.data;
};

export const updateUserProfile = async (data) => {
  const response = await axiosInstance.patch('/account/api/accounts/profile/', data);
  return response.data;
};

export const resendVerification = async (email) => {
  const response = await axiosInstance.post('/account/auth/resend-verification/', { email });
  return response.data;
};

export const verifyEmail = async (token) => {
  const response = await axiosInstance.post('/account/auth/verify-email/', { token });
  return response.data;
};

export const checkVerifyToken = async (token) => {
  const response = await axiosInstance.get(`/account/auth/verify-email/?token=${token}`);
  return response.data;
};

export const changePassword = async (data) => {
  const response = await axiosInstance.post('/account/auth/change-password/', data);
  return response.data;
};

export const getUserFamiliesDetailed = async () => {
  const response = await axiosInstance.get('/account/families/detailed/');
  return response.data;
};

export const getPendingInvitations = async () => {
  const response = await axiosInstance.get('/account/api/accounts/pending_invitations/');
  return response.data;
};

export const setPrimaryFamily = async (familyId) => {
  const response = await axiosInstance.post('/account/set-primary-family/', { family_id: familyId });
  return response.data;
};

export const createNewFamily = async (name) => {
  const response = await axiosInstance.post('/account/families/create/', { name });
  return response.data;
};

export const joinFamilyWithCode = async (shareToken) => {
  const response = await axiosInstance.post('/account/join-additional-family/', { share_token: shareToken });
  return response.data;
};

export const getLifeEvents = async () => {
  try {
    const response = await axiosInstance.get('/lifeevent/events/')
    return response.data
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data)
      throw new Error(error.response.data.message || JSON.stringify(error.response.data))
    } else if (error.request) {
      console.error("Error request:", error.request)
      throw new Error('No response received from server')
    } else {
      console.error("Error message:", error.message)
      throw new Error('An unexpected error occurred')
    }
  }
}

export const getLifeEventTemplates = async () => {
  try {
    const response = await axiosInstance.get('/lifeevent/templates/')
    return response.data
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data)
      throw new Error(error.response.data.message || JSON.stringify(error.response.data))
    } else if (error.request) {
      console.error("Error request:", error.request)
      throw new Error('No response received from server')
    } else {
      console.error("Error message:", error.message)
      throw new Error('An unexpected error occurred')
    }
  }
}

export const createLifeEvent = async (eventData) => {
  try {
    const response = await axiosInstance.post('/lifeevent/events/', eventData)
    return response.data
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data)
      throw new Error(error.response.data.message || JSON.stringify(error.response.data))
    } else if (error.request) {
      console.error("Error request:", error.request)
      throw new Error('No response received from server')
    } else {
      console.error("Error message:", error.message)
      throw new Error('An unexpected error occurred')
    }
  }
}

// Create a new person with relationship to existing person
export const createPersonWithRelationship = async (personData) => {
  try {
    // Check if auth token exists
    const token = ls.get('authToken');
    
    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }

    const response = await axiosInstance.post('/genealogy/people/create_with_relationship/', personData);
    return response.data;
  } catch (error) {
    console.error('Create person with relationship API error:', error);
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    throw new Error(error.response?.data?.message || error.message || 'An unexpected error occurred');
  }
};

// Create a new person only (no relationship)
export const createPerson = async (personData) => {
  try {
    const token = ls.get('authToken');
    
    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }

    const response = await axiosInstance.post('/genealogy/people/create/', personData);
    return response.data;
  } catch (error) {
    console.error('Create person API error:', error);
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    throw new Error(error.response?.data?.message || error.message || 'An unexpected error occurred');
  }
};

// Create relationship between two existing people (UPDATED GEDCOM API)
export const createRelationship = async (relationshipData) => {
  try {
    const token = ls.get('authToken');
    
    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }

    const response = await axiosInstance.post('/genealogy/people/create_relationship/', relationshipData);
    return response.data;
  } catch (error) {
    console.error('Create relationship API error:', error);
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    throw new Error(error.response?.data?.error || error.response?.data?.message || error.message || 'An unexpected error occurred');
  }
};

// Helper function: Create person and then establish relationship (combines both steps)
export const createPersonAndRelationship = async (personData, existingPersonId, relationshipType) => {
  try {
    
    // Step 1: Create the new person
    const newPerson = await createPerson(personData);
    
    // Step 2: Create relationship between existing person and new person
    const relationshipPayload = {
      person1_id: existingPersonId,
      person2_id: newPerson.id,
      relationship_type: relationshipType,
      family_context: "auto"
    };
    
    const relationship = await createRelationship(relationshipPayload);
    
    return {
      person: newPerson,
      relationship: relationship,
      success: true
    };
  } catch (error) {
    console.error('❌ Create person and relationship failed:', error);
    throw error;
  }
};

// ==================== Milestone API Functions ====================

// Get all milestone templates (standard library)
export const getMilestoneTemplates = async () => {
  try {
    const response = await axiosInstance.get('/milestone-templates/');
    return response.data;
  } catch (error) {
    console.error('Error fetching milestone templates:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch milestone templates');
  }
};

// Get milestone templates grouped by category
export const getMilestoneTemplatesByCategory = async () => {
  try {
    const response = await axiosInstance.get('/milestone-templates/by_category/');
    return response.data;
  } catch (error) {
    console.error('Error fetching milestone templates by category:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch milestone templates');
  }
};

// Get all milestones the user has access to
export const getMilestones = async () => {
  try {
    const response = await axiosInstance.get('/milestones/');
    return response.data;
  } catch (error) {
    console.error('Error fetching milestones:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch milestones');
  }
};

// Get milestones for a specific person
export const getMilestonesForPerson = async (personId) => {
  try {
    const response = await axiosInstance.get(`/milestones/for_person/?person_id=${personId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching milestones for person:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch milestones');
  }
};

// Create a new milestone
export const createMilestone = async (milestoneData) => {
  try {
    const response = await axiosInstance.post('/milestones/', milestoneData);
    return response.data;
  } catch (error) {
    console.error('Error creating milestone:', error);
    throw new Error(error.response?.data?.message || 'Failed to create milestone');
  }
};

// Create a milestone from a template
export const createMilestoneFromTemplate = async (templateId, personId, targetDate, fallbackDate) => {
  try {
    const response = await axiosInstance.post('/milestones/create_from_template/', {
      template_id: templateId,
      person_id: personId,
      target_date: targetDate,
      fallback_date: fallbackDate
    });
    return response.data;
  } catch (error) {
    console.error('Error creating milestone from template:', error);
    throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to create milestone');
  }
};

// Update a milestone
export const updateMilestone = async (milestoneId, milestoneData) => {
  try {
    const response = await axiosInstance.patch(`/milestones/${milestoneId}/`, milestoneData);
    return response.data;
  } catch (error) {
    console.error('Error updating milestone:', error);
    throw new Error(error.response?.data?.message || 'Failed to update milestone');
  }
};

// Cancel a milestone
export const cancelMilestone = async (milestoneId) => {
  try {
    const response = await axiosInstance.post(`/milestones/${milestoneId}/cancel/`);
    return response.data;
  } catch (error) {
    console.error('Error cancelling milestone:', error);
    throw new Error(error.response?.data?.message || 'Failed to cancel milestone');
  }
};

// Delete a milestone
export const deleteMilestone = async (milestoneId) => {
  try {
    const response = await axiosInstance.delete(`/milestones/${milestoneId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting milestone:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete milestone');
  }
};

// ==================== Profile Photo API Functions ====================

// Upload profile photo for a family member
export const uploadProfilePhoto = async (familyId, memberId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post(
      `/families/${familyId}/members/${memberId}/profile-photo/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw new Error(error.response?.data?.error || 'Failed to upload profile photo');
  }
};

// Delete profile photo for a family member
export const deleteProfilePhoto = async (familyId, memberId) => {
  try {
    const response = await axiosInstance.delete(
      `/families/${familyId}/members/${memberId}/profile-photo/`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting profile photo:', error);
    throw new Error(error.response?.data?.error || 'Failed to delete profile photo');
  }
};

// ============================================================================
// Relationship Inference Suggestions API
// ============================================================================

/**
 * Get all pending relationship suggestions for the current user
 */
export const getPendingSuggestions = async () => {
  try {
    const response = await axiosInstance.get('/genealogy/suggestions/pending/');
    return response.data;
  } catch (error) {
    console.error('Error fetching pending suggestions:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch suggestions');
  }
};

/**
 * Accept a single relationship suggestion
 * @param {string} suggestionId - UUID of the suggestion to accept
 */
export const acceptSuggestion = async (suggestionId) => {
  try {
    const response = await axiosInstance.post(`/genealogy/suggestions/${suggestionId}/accept/`);
    return response.data;
  } catch (error) {
    console.error('Error accepting suggestion:', error);
    throw new Error(error.response?.data?.error || 'Failed to accept suggestion');
  }
};

/**
 * Reject a single relationship suggestion
 * @param {string} suggestionId - UUID of the suggestion to reject
 */
export const rejectSuggestion = async (suggestionId) => {
  try {
    const response = await axiosInstance.post(`/genealogy/suggestions/${suggestionId}/reject/`);
    return response.data;
  } catch (error) {
    console.error('Error rejecting suggestion:', error);
    throw new Error(error.response?.data?.error || 'Failed to reject suggestion');
  }
};

/**
 * Bulk accept/reject multiple suggestions at once
 * @param {Object} actions - Object with 'accept' and 'reject' arrays of suggestion IDs
 * @param {string[]} actions.accept - Array of suggestion IDs to accept
 * @param {string[]} actions.reject - Array of suggestion IDs to reject
 */
export const processSuggestionsBulk = async ({ accept = [], reject = [] }) => {
  try {
    const response = await axiosInstance.post('/genealogy/suggestions/bulk/', {
      accept,
      reject
    });
    return response.data;
  } catch (error) {
    console.error('Error processing suggestions:', error);
    throw new Error(error.response?.data?.error || 'Failed to process suggestions');
  }
};
